import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { createAuthHandoff, exchangeAuthHandoff } from '../api/authHandoffApi';
import getBaseUrl from '../utils/getBaseUrl';
import safeLocalReturnPath from '../utils/safeReturnPath';
import { productOrigin, AI_ORIGIN, SPACE_ORIGIN, GATEWAY_ORIGIN } from '../utils/productOrigins';
import './AuthHandoffPage.css';

const AI_RETURN_PATH_KEY = 'doar_ai_return_path';
const RETURN_PATH_KEY = 'doar_return_path';

const COM_ORIGIN = productOrigin(process.env.REACT_APP_ACCOUNT_URL, 3002, 'https://www.thedoar.com');

function canonicalProductOrigins() {
  const values = [
    COM_ORIGIN,
    'https://thedoar.com',
    'https://www.thedoar.com',
    AI_ORIGIN,
    'https://thedoar.ai',
    'https://www.thedoar.ai',
    SPACE_ORIGIN,
    'https://doarspace.com',
    'https://www.doarspace.com',
    GATEWAY_ORIGIN,
    'https://todoar.co',
    'https://www.todoar.co',
    'http://localhost:3000',
    'http://localhost:3001',
  ];
  return [...new Set(values.map((value) => {
    try { return new URL(value).origin; } catch { return null; }
  }).filter(Boolean))];
}

function safeTarget(value) {
  try {
    const origin = new URL(value).origin;
    return canonicalProductOrigins().includes(origin) ? origin : null;
  } catch {
    return null;
  }
}

function safeReturnPath(value) {
  return safeLocalReturnPath(value) || '/';
}
function safeAiReturnPath(value) {
  return safeReturnPath(value);
}

function resumeUrl(targetOrigin) {
  const apiBase = String(getBaseUrl() || '').replace(/\/+$/, '');
  return `${apiBase}/auth/handoff/resume?targetOrigin=${encodeURIComponent(targetOrigin)}`;
}

function completeUrl(targetOrigin, code) {
  const destination = new URL('/auth/complete', targetOrigin);
  destination.searchParams.set('code', code);
  return destination.toString();
}

function shouldDeferHandoff(loading) {
  return Boolean(loading);
}

function handoffError(err) {
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) return serverMessage;
  if (!err?.response) return 'TheDoAR could not reach the account service. Check the connection and try again.';
  return err?.message || 'Could not continue your TheDoAR session.';
}

export default function AuthHandoffPage({ mode }) {
  const { login, token, loading } = useContext(AuthContext);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // AuthContext may be consuming the one-time Google handoff code on this
      // exact route. Navigating to /resume before that exchange finishes
      // cancels it and creates a login loop, especially on mobile/PWA flows.
      if (shouldDeferHandoff(loading)) return;

      try {
        if (mode === 'complete') {
          const params = new URLSearchParams(window.location.search);
          const code = params.get('code');
          if (!code) throw new Error('Missing sign-in handoff.');
          const result = await exchangeAuthHandoff(code, window.location.origin);
          if (cancelled) return;
          login(result.token);
          const returnPath = safeReturnPath(
            sessionStorage.getItem(RETURN_PATH_KEY) ||
            sessionStorage.getItem(AI_RETURN_PATH_KEY)
          );
          sessionStorage.removeItem(RETURN_PATH_KEY);
          sessionStorage.removeItem(AI_RETURN_PATH_KEY);
          window.history.replaceState({}, '', returnPath);
          window.location.replace(returnPath);
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const targetOrigin = safeTarget(params.get('target') || AI_ORIGIN);
        if (!targetOrigin) throw new Error('Invalid DoAR handoff destination.');

        if (token) {
          const result = await createAuthHandoff(targetOrigin);
          if (cancelled) return;
          if (!result?.code) throw new Error('Could not create sign-in handoff.');
          window.location.replace(completeUrl(targetOrigin, result.code));
          return;
        }

        window.location.replace(resumeUrl(targetOrigin));
      } catch (err) {
        if (!cancelled) setError(handoffError(err));
      }
    };

    run();
    return () => { cancelled = true; };
  }, [loading, login, mode, token]);

  return (
    <main className="handoff-root">
      <a className="handoff-brand" href={mode === 'complete' ? window.location.origin : COM_ORIGIN}>TheDoAR</a>
      <div className="handoff-card" role="status" aria-live="polite">
        {error ? (
          <>
            <strong>We couldn't continue your session.</strong>
            <p>{error}</p>
            <a href={`${COM_ORIGIN}/login`}>Sign in to TheDoAR</a>
          </>
        ) : (
          <>
            <span className="handoff-spinner" aria-hidden="true" />
            <strong>Signing you in…</strong>
            <p>One DoAR account across every DoAR surface.</p>
          </>
        )}
      </div>
    </main>
  );
}

export { safeAiReturnPath, safeReturnPath, safeTarget, canonicalProductOrigins, shouldDeferHandoff, RETURN_PATH_KEY };
